import CookieHelper from './cookieHelper';
import FieldTypesEnum from '../Enums/FieldTypesEnum';
import RoleService from '../Services/roleService';

// CD-5255 Column Selector: derives the toggleable column list, stable per-column keys,
// locked flags, and reads/writes the per-page "hidden columns" cookie. Shared by the hub
// (TabOfDataTable), the toolbar strip (GeneralSearchStrip), and the table cells
// (DataTableHeader / DataTableBody) so they all agree on the same keys.
// Imports are .js modules (typed as `any`); column field objects are untyped, hence `any`.

export interface SelectableColumn {
  key: string;
  label: string;
  locked: boolean;
}

const COOKIE_PREFIX = 'cols_';

// Stable, minification-safe key for a column definition. Prefer the backend sort key,
// then a string field name, then the label; fall back to the array index. The hub and the
// table iterate the SAME FieldsToShowInDataTable() array in the same order, so the index
// fallback stays consistent across header/body/selector.
function getColumnKey(field: any, index: number): string {
  if (field && field.orderById) return String(field.orderById);
  if (field && typeof field.data === 'string' && field.data !== '') return field.data;
  if (field && typeof field.label === 'string' && field.label !== '') return field.label;
  return `idx${index}`;
}

// Page identity for the cookie: an explicit per-page id if defined, else the route path
// (stable, unique per page, and NOT subject to class-name minification in the prod build).
function getPageKey(type: any): string {
  if (type && type.uniquePageIdForColumnCookies) return type.uniquePageIdForColumnCookies;
  return window.location.pathname;
}

function getCookieName(type: any): string {
  return COOKIE_PREFIX + getPageKey(type);
}

// Resolve a column's display label to a translated string, or '' when there is none.
// Only a genuine text header names a toggleable column. A label() that returns a React
// element (e.g. the row-select checkbox header, whose String() is "[object Object]") is a
// structural column, so anything that isn't a string/number counts as "no label". Likewise
// whitespace-only labels (a layout spacer's " ") are treated as no label. All such
// structural columns are excluded from the selector, just like unlabelled ones.
function getColumnLabel(field: any): string {
  const raw = field && typeof field.label === 'function' ? field.label() : field && field.label;
  if (typeof raw !== 'string' && typeof raw !== 'number') return '';
  if (String(raw).trim() === '') return '';
  return String(raw);
}

// A column is "listable" (appears in the selector) when it renders in normal view AND has
// a usable label. Structural columns are excluded: Navigation (already hidden in normal
// view), ActionButton (label-less action cell), role-hidden columns, and any unlabelled
// column (e.g. the select-checkbox column).
function isListable(field: any): boolean {
  if (!field) return false;
  if (field.type === FieldTypesEnum.Navigation) return false;
  if (field.type === FieldTypesEnum.ActionButton) return false;
  if (field.rolekey && !RoleService.CheckUserPermission('datatable', field.rolekey)) return false;
  if (getColumnLabel(field) === '') return false;
  return true;
}

// Build the selector list. The first listable column (the identifier) is locked, as is any
// column flagged alwaysVisible.
function getSelectableColumns(type: any): SelectableColumn[] {
  if (!type || typeof type.FieldsToShowInDataTable !== 'function') return [];
  const fields = type.FieldsToShowInDataTable();
  if (!Array.isArray(fields)) return [];
  const result: SelectableColumn[] = [];
  let firstListableSeen = false;
  fields.forEach((field: any, index: number) => {
    if (!isListable(field)) return;
    const locked = !firstListableSeen || field.alwaysVisible === true;
    firstListableSeen = true;
    result.push({ key: getColumnKey(field, index), label: getColumnLabel(field), locked });
  });
  return result;
}

// True when at least one listable column is NOT locked (i.e. the button is worth showing).
function hasToggleableColumns(type: any): boolean {
  return getSelectableColumns(type).some((c) => !c.locked);
}

// Read the per-page hidden-column keys into a Set.
function readHiddenColumns(type: any): Set<string> {
  const raw = CookieHelper.getCookie(getCookieName(type), '');
  if (!raw) return new Set<string>();
  return new Set<string>(String(raw).split(',').filter(Boolean));
}

// Persist the per-page hidden-column keys.
function writeHiddenColumns(type: any, hiddenSet: Set<string>): void {
  CookieHelper.setCookie(getCookieName(type), Array.from(hiddenSet).join(','));
}

// Clear the per-page selection (Reset to default).
function clearHiddenColumns(type: any): void {
  CookieHelper.setCookie(getCookieName(type), '');
}

const columnVisibilityHelper = {
  getColumnKey,
  getPageKey,
  getColumnLabel,
  getSelectableColumns,
  hasToggleableColumns,
  readHiddenColumns,
  writeHiddenColumns,
  clearHiddenColumns,
};

export default columnVisibilityHelper;
